import React, {useState, useMemo, useEffect} from "react"
import {Paper, Box, useTheme} from "@mui/material"
import {useNavigate} from "react-router-dom"
import {ReactComponent as CheckIcon} from "src/v2/assets/icons/check-icon.svg"
import {ReactComponent as EditIcon} from "src/v2/assets/icons/lock-empty-icon.svg"
import FormActions from "../../components/FormActions"
import {dispatchSnackbar} from "../../../../components/SnackBar/snackbar-bus"
import MotorEventosOrdinariosService from "src/services/motor-eventos-ordinarios-service"
import EventosOrdinariosTable from "./components/eventos-ordinarios/eventos-ordinarios-table"
import FormSectionAccordion from "../../../../components/FormSectionAccordion"
import DataAniversarioTable from "./components/data-aniversario/data-aniversario-table"
import {
    mapEventosOrdinariosPayload,
    mapEventosOrdinariosToTable
} from "./components/eventos-ordinarios/map-eventos-ordinarios"
import MotorDataAniversarioService from "src/services/motor-data-aniversario-service"
import PremioTable from "./components/premio/premio-table"
import {mapPremioPayload} from "./components/premio/premio-fields"
import MotorPremioService from "../../../../../services/motor-premio-service"
import {mapDataAniversarioPayload} from "./components/data-aniversario/data-aniversario-fields"
import RecompraTable from "./components/recompra-programada/recompra-programada-table"
import {mapRecompraProgramadaPayload} from "./components/recompra-programada/recompra-programada-fields"
import ConversabilidadeTable from "./components/conversibilidade/conversabilidade-table"
import {mapConversibilidadePayload} from "./components/conversibilidade/conversabilidade-fields"
import MotorRecompraProgramadaService from "../../../../../services/motor-recompra-programada-service"
import MotorConversibilidadeService from "../../../../../services/motor-conversibilidade-service"
import DuplaConfirmacao from "../../../../components/DuplaConfirmacao/DuplaConfirmacao"
import PropTypes from "prop-types"

const TABLE_DATA_DEFAULT = [{}]
const INDEXADORES_DATA_ANIVERSARIO = [
    "INCC",
    "TR",
    "IPC_FIPE",
    "IPCA",
    "INPC",
    "INCC_DI",
    "IGPDI",
    "IGPM"
]

const EventosOrdinariosPage = (
    {
        idCaracteristica,
        producao,
        setProducao,
        eventosProjetados
    }) => {
    const eventosService = useMemo(() => new MotorEventosOrdinariosService(), [])
    const dataAniversarioService = useMemo(() => new MotorDataAniversarioService(), [])
    const premioService = useMemo(() => new MotorPremioService(), [])
    const recompraProgramadaService = useMemo(() => new MotorRecompraProgramadaService(), [])
    const conversibilidadeService = useMemo(() => new MotorConversibilidadeService(), [])

    const [dataAniversarioPublicado, setDataAniversarioPublicado] = useState(false)
    const [premioPublicado, setPremioPublicado] = useState(false)
    const [recompraPublicado, setRecompraPublicado] = useState(false)
    const [conversibilidadePublicado, setConversibilidadePublicado] = useState(false)

    const theme = useTheme()
    const [loading, setLoading] = useState(false)
    const navigate = useNavigate()
    const [tableEventosData, setTableEventosData] = useState(TABLE_DATA_DEFAULT)
    const [tableAniversarioData, setTableAniversarioData] = useState(TABLE_DATA_DEFAULT)
    const [tablePremioData, setTablePremioData] = useState(TABLE_DATA_DEFAULT)
    const [tableRecompraProgramadaData, setTableRecompraProgramadaData] = useState(TABLE_DATA_DEFAULT)
    const [tableConversibilidadeData, setTableConversibilidadeData] = useState(TABLE_DATA_DEFAULT)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [publishConfirmOpen, setPublishConfirmOpen] = useState(false)
    const [showDataAniversario, setShowDataAniversario] = useState(false)
    const [editarOpen, setEditarOpen] = useState(false)

    const handleEditarClick = (e) => {
        e?.preventDefault()
        e?.stopPropagation()
        setEditarOpen(true)
    }

    const handleConfirmEditar = () => {
        setEditarOpen(false)
        handlePublish()
    }

    const handleCancelEditar = () => {
        setEditarOpen(false)
    }

    const handleSave = async () => {
        setLoading(true)

        try {
            const eventoOrdinario = mapEventosOrdinariosPayload(tableEventosData)
            const dataAniversario = mapDataAniversarioPayload(tableAniversarioData, idCaracteristica)
            const premio = mapPremioPayload(tablePremioData, idCaracteristica)
            const recompraProgramada = mapRecompraProgramadaPayload(tableRecompraProgramadaData, idCaracteristica)
            const conversibilidade = mapConversibilidadePayload(tableConversibilidadeData, idCaracteristica)

            const requests = [
                {
                    key: "eventosOrdinarios",
                    label: "Eventos Ordinários",
                    promise: eventosService.createCadastroTemporarioEventosOrdinarios(idCaracteristica, eventoOrdinario),
                },
                {
                    key: "dataAniversario",
                    label: "Data Aniversário",
                    promise: dataAniversarioService.createCadastroTemporarioDataAniversario(idCaracteristica, dataAniversario),
                },
                {
                    key: "premio",
                    label: "Prêmio",
                    promise: premioService.createCadastroTemporarioPremio(idCaracteristica, premio),
                },
                {
                    key: "recompraProgramada",
                    label: "Recompra Programada",
                    promise: recompraProgramadaService.createCadastroTemporarioRecompraProgramada(idCaracteristica, recompraProgramada),
                },
                {
                    key: "conversibilidade",
                    label: "Conversibilidade",
                    promise: conversibilidadeService.createCadastroTemporarioConversibilidade(idCaracteristica, conversibilidade),
                },
            ]

            const results = await Promise.allSettled(
                requests.map((item) => item.promise)
            )

            const responseSummary = requests.map((request, index) => {
                const result = results[index]

                if (result.status === "fulfilled") {
                    return {
                        key: request.key,
                        label: request.label,
                        success: true,
                        data: result.value?.body ?? result.value ?? null,
                        error: null,
                    }
                }

                return {
                    key: request.key,
                    label: request.label,
                    success: false,
                    data:
                        result.reason?.response?.text ??
                        `Erro ao salvar ${request.label}`,
                    error:
                        result.reason?.response?.data?.erro ||
                        result.reason?.response?.data?.error ||
                        result.reason?.message ||
                        "Erro desconhecido",
                }
            })

            const errors = responseSummary.filter((item) => !item.success)
            if (errors.length > 0) {
                errors.forEach((item) => {
                    dispatchSnackbar(item.data || `Erro ao salvar ${item.label}`, "error")
                })
                return
            }

            dispatchSnackbar("Todos os dados foram salvos com sucesso!", "success")
        } finally {
            setLoading(false)
        }
    }

    const buildRequest = ({key, label, published, createFn, updateFn}) => ({
        key,
        label,
        promise: published ? updateFn : createFn,
    })

    const buildRequestDelete = ({key, label, published, deleteTemp, deleteProd}) => ({
        key,
        label,
        promise: published ? deleteProd : deleteTemp,
    })

    const handlePublish = async () => {
        try {
            setLoading(true)
            const {EventosOrdinarios} = mapEventosOrdinariosPayload(tableEventosData)
            const {DatasAniversario} = mapDataAniversarioPayload(tableAniversarioData, idCaracteristica)
            const Premios = mapPremioPayload(tablePremioData, idCaracteristica)
            const RecomprasProgramadas = mapRecompraProgramadaPayload(tableRecompraProgramadaData, idCaracteristica)
            const {Conversibilidades} = mapConversibilidadePayload(tableConversibilidadeData, idCaracteristica)

            const requests = [
                buildRequest({
                    key: "eventosOrdinarios",
                    label: "Eventos Ordinários",
                    published: producao,
                    createFn: () => eventosService.createCadastroEventosOrdinarios(idCaracteristica, EventosOrdinarios),
                    updateFn: () => eventosService.updateCadastroEventosOrdinarios(idCaracteristica, EventosOrdinarios),
                }),
                buildRequest({
                    key: "dataAniversario",
                    label: "Data Aniversário",
                    published: dataAniversarioPublicado,
                    createFn: () => dataAniversarioService.createCadastroDataAniversario(idCaracteristica, DatasAniversario),
                    updateFn: () => dataAniversarioService.updateCadastroDataAniversario(idCaracteristica, DatasAniversario),
                }),
                buildRequest({
                    key: "premio",
                    label: "Prêmio",
                    published: premioPublicado,
                    createFn: () => premioService.createCadastroPremio(idCaracteristica, Premios),
                    updateFn: () => premioService.updateCadastroPremio(idCaracteristica, Premios),
                }),
                buildRequest({
                    key: "recompraProgramada",
                    label: "Recompra Programada",
                    published: recompraPublicado,
                    createFn: () => recompraProgramadaService.createCadastroRecompraProgramada(idCaracteristica, RecomprasProgramadas),
                    updateFn: () => recompraProgramadaService.updateCadastroRecompraProgramada(idCaracteristica, RecomprasProgramadas),
                }),
                buildRequest({
                    key: "conversibilidade",
                    label: "Conversibilidade",
                    published: conversibilidadePublicado,
                    createFn: () => conversibilidadeService.createCadastroConversibilidade(idCaracteristica, Conversibilidades),
                    updateFn: () => conversibilidadeService.updateCadastroConversibilidade(idCaracteristica, Conversibilidades),
                }),
            ]

            const results = await Promise.allSettled(
                requests.map((item) => item.promise())
            )

            const responseSummary = requests.map((request, index) => {
                const result = results[index]

                if (result.status === "fulfilled") {
                    return {
                        key: request.key,
                        label: request.label,
                        success: true,
                        data: result.value?.body ?? result.value ?? null,
                        error: null,
                    }
                }

                return {
                    key: request.key,
                    label: request.label,
                    success: false,
                    data:
                        result.reason?.response?.text ??
                        `Erro ao salvar ${request.label}`,
                    error:
                        result.reason?.response?.data?.erro ||
                        result.reason?.response?.data?.error ||
                        result.reason?.message ||
                        "Erro desconhecido",
                }
            })

            const errors = responseSummary.filter((item) => !item.success)
            if (errors.length > 0) {
                errors.forEach((item) => {
                    dispatchSnackbar(item.data || `Erro ao salvar ${item.label}`, "error")
                })
                return
            }

            setProducao(true)
            dispatchSnackbar(
                producao
                    ? "Cadastro atualizado em produção com sucesso."
                    : "Cadastro publicado com sucesso.",
                "success"
            )
            setProducao(true)
        } finally {
            setPublishConfirmOpen(false)
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        setLoading(true)

        try {
            const requests = [
                buildRequestDelete({
                    key: "eventosOrdinarios",
                    label: "Eventos Ordinários",
                    published: producao,
                    deleteTemp: () => eventosService.deleteCadastroTemporarioEventosOrdinarios(idCaracteristica),
                    deleteProd: () => eventosService.deleteCadastroEventosOrdinarios(idCaracteristica),
                }),
                buildRequestDelete({
                    key: "dataAniversario",
                    label: "Data Aniversário",
                    published: producao,
                    deleteTemp: () => dataAniversarioService.deleteCadastroTemporarioDataAniversario(idCaracteristica),
                    deleteProd: () => dataAniversarioService.deleteCadastroDataAniversario(idCaracteristica),
                }),
                buildRequestDelete({
                    key: "premio",
                    label: "Prêmio",
                    published: producao,
                    deleteTemp: () => premioService.deleteCadastroTemporarioPremio(idCaracteristica),
                    deleteProd: () => premioService.deleteCadastroPremio(idCaracteristica),
                }),
                buildRequestDelete({
                    key: "recompraProgramada",
                    label: "Recompra Programada",
                    published: producao,
                    deleteTemp: () => recompraProgramadaService.deleteCadastroTemporarioRecompraProgramada(idCaracteristica),
                    deleteProd: () => recompraProgramadaService.deleteCadastroRecompraProgramada(idCaracteristica),
                }),
                buildRequestDelete({
                    key: "conversibilidade",
                    label: "Conversibilidade",
                    published: producao,
                    deleteTemp: () => conversibilidadeService.deleteCadastroTemporarioConversibilidade(idCaracteristica),
                    deleteProd: () => conversibilidadeService.deleteCadastroConversibilidade(idCaracteristica),
                }),
            ]

            const results = await Promise.allSettled(
                requests.map((item) => item.promise())
            )

            const responseSummary = requests.map((request, index) => {
                const result = results[index]

                if (result.status === "fulfilled") {
                    return {
                        key: request.key,
                        label: request.label,
                        success: true,
                        data: result.value?.body ?? result.value ?? null,
                        error: null,
                    }
                }

                return {
                    key: request.key,
                    label: request.label,
                    success: false,
                    data:
                        result.reason?.response?.text ??
                        `Erro ao salvar ${request.label}`,
                    error:
                        result.reason?.response?.data?.erro ||
                        result.reason?.response?.data?.error ||
                        result.reason?.message ||
                        "Erro desconhecido",
                }
            })

            const errors = responseSummary.filter((item) => !item.success)
            if (errors.length > 0) {
                errors.forEach((item) => {
                    dispatchSnackbar(item.data || `Erro ao salvar ${item.label}`, "error")
                })
                return
            }

            dispatchSnackbar("Todos os dados foram removidos com sucesso!", "success")
        } finally {
            setPublishConfirmOpen(false)
            setLoading(false)
            window.location.reload()
        }
    }

    const clearTable = (setClearFunction) => {
        setClearFunction(TABLE_DATA_DEFAULT)
    }
    const activeDataAniversario = (value) => {
        if (value) {
            dispatchSnackbar("Avaliar aplicabilidade de preenchimento de Data Aniversário", "warning")
        }
        setShowDataAniversario(value)
    }

    const setTableEventosItem = (data) => {
        const dataAtualizada = data.map((item, index) => {
            const itemAnterior = tableEventosData[index]

            // 🔒 evita recalcular na primeira carga (colar/projetar)
            if (!itemAnterior) {
                return item
            }
            return item
        })

        setTableEventosData(dataAtualizada)

        const indexadorFound = dataAtualizada.some(item =>
            INDEXADORES_DATA_ANIVERSARIO.includes(item.Indexador)
        )

        if (!showDataAniversario && indexadorFound) {
            activeDataAniversario(true)
            return
        }

        activeDataAniversario(false)
    }

    useEffect(() => {
        if (eventosProjetados !== null) {
            const eventosProjetadosMapped = mapEventosOrdinariosToTable(eventosProjetados)
            setTableEventosData(eventosProjetadosMapped)
        }
    }, [eventosProjetados])

    useEffect(() => {

    })

    return (
        <>
            <Paper sx={{pt: 3, pb: 2, pl: 4, pr: 4, mb: (showDataAniversario ? 0 : 2)}}>
                <EventosOrdinariosTable
                    idCaracteristica={idCaracteristica}
                    setLoading={setLoading}
                    tableData={tableEventosData}
                    clearTable={clearTable}
                    eventoPublicado={setProducao}
                    setTableData={setTableEventosItem}
                />
            </Paper>

            {showDataAniversario && (
                <Box sx={{mt: 2, mb: 2}}>
                    <FormSectionAccordion
                        title="Data Aniversário"
                        defaultOpen={true}
                        disableBorder={true}
                    >
                        <DataAniversarioTable
                            setLoading={setLoading}
                            idCaracteristica={idCaracteristica}
                            dataAniversarioPublicado={setDataAniversarioPublicado}
                            showDataAniversario={showDataAniversario}
                            tableData={tableAniversarioData}
                            setTableData={setTableAniversarioData}
                            clearTable={clearTable}
                        />
                    </FormSectionAccordion>
                </Box>
            )}

            <Box sx={{mb: 2}}>
                <FormSectionAccordion
                    title="Prêmio"
                    defaultOpen={true}
                    disableBorder={true}
                >
                    <PremioTable
                        setLoading={setLoading}
                        idCaracteristica={idCaracteristica}
                        premioPublicado={setPremioPublicado}
                        tableData={tablePremioData}
                        setTableData={setTablePremioData}
                        clearTable={clearTable}
                    />
                </FormSectionAccordion>
            </Box>

            <Box sx={{mb: 2}}>
                <FormSectionAccordion
                    title="Recompra Programada"
                    defaultOpen={true}
                    disableBorder={true}
                >
                    <RecompraTable
                        setLoading={setLoading}
                        idCaracteristica={idCaracteristica}
                        recompraPublicado={setRecompraPublicado}
                        tableData={tableRecompraProgramadaData}
                        setTableData={setTableRecompraProgramadaData}
                        clearTable={clearTable}
                    />
                </FormSectionAccordion>
            </Box>

            <Box sx={{mb: 8}}>
                <FormSectionAccordion
                    title="Conversibilidade"
                    defaultOpen={true}
                    disableBorder={true}
                >
                    <ConversabilidadeTable
                        setLoading={setLoading}
                        idCaracteristica={idCaracteristica}
                        conversibilidadePublicado={setConversibilidadePublicado}
                        tableData={tableConversibilidadeData}
                        setTableData={setTableConversibilidadeData}
                        clearTable={clearTable}
                    />
                </FormSectionAccordion>
            </Box>

            <Box sx={{mx: {xxxs: -3, md: -3, lg: 0}, mt: 4}}>
                <FormActions
                    showBack
                    onBack={() => navigate(-1)}
                    onDelete={() => setDeleteConfirmOpen(true)}
                    deleteOpen={deleteConfirmOpen}
                    setDeleteOpen={setDeleteConfirmOpen}
                    onConfirmDelete={handleDelete}
                    showSave={!producao}
                    onSave={!loading ? handleSave : undefined}
                    onPublish={
                        producao
                            ? handleEditarClick
                            : () => setPublishConfirmOpen(true)
                    }
                    publishOpen={publishConfirmOpen}
                    setPublishOpen={setPublishConfirmOpen}
                    onConfirmPublish={handlePublish}
                    publishLabel={
                        producao
                            ? "EDITAR"
                            : "PUBLICAR"
                    }
                    publishIcon={
                        producao
                            ? <EditIcon/>
                            : <CheckIcon/>
                    }
                    theme={theme}/>
            </Box>

            <DuplaConfirmacao
                open={editarOpen}
                cancel={handleCancelEditar}
                confirm={handleConfirmEditar}
                title="Editar Eventos"
                description="
                        Certifique-se de que todas as alterações foram feitas,
                        pois você está alterando um registro em produção.
                    "
            />
        </>
    )
}

EventosOrdinariosPage.propTypes = {
    idCaracteristica: PropTypes.number,
    caracteristica: PropTypes.object,
    cetip: PropTypes.string,
    versaoDocumento: PropTypes.number,
    producao: PropTypes.object,
    setProducao: PropTypes.func,
    eventosProjetados: PropTypes.array,
}

export default EventosOrdinariosPage
